import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { HiOutlineArrowLeft } from "react-icons/hi";
import {
  GoogleMap,
  Marker,
  Polygon,
  useJsApiLoader,
} from "@react-google-maps/api";
import toast from "react-hot-toast";
import { Formik, Form } from "formik";
import * as attendanceService from "../../Services/attendanceService";
import { FloatingInput, Loader } from "../../Components/Common";
import { ATTENDANCE_DASHBOARD_LINK } from "../../routes";
import { UserContext } from "../../Context";
import { ACTION_APPROVED, ACTION_REJECTED, AUTO_APPROVED, MESSAGES } from "./constants";
import RejectPopup from "./Component/RejectPopup";
import { HiViewfinderCircle } from "react-icons/hi2";
import { BiHide } from "react-icons/bi";
import { formatTimeAMPM } from "../../Utils/timeUtils";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { formatDateDDMMYYYY } from "../../Utils";
import { parseWKTPolygon } from "../../Utils/geoUtils";

const libraries = ["places"];
const DetailedViewPage = () => {
  const { id } = useParams();
  const [attendance, setAttendance] = useState({
    site_name: "",
    user_name: "",
    date: "",
    checkin_time: "",
    checkout_time: "",
    is_backdated: "",
    status: "",
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { nonStaffAccess } = useContext(UserContext);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [showCheckinPhoto, setShowCheckinPhoto] = useState(false);
  const [showCheckoutPhoto, setShowCheckoutPhoto] = useState(false);

  const location = useLocation();
  const filters = location.state?.filters || { status: "", date: null, user: "" };

  // For Google Map
  const containerStyle = {
    width: "100%",
    height: "400px",
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const handleApproval = async (id, action, rejection_reason) => {
    try {
      const response = await attendanceService.reviewAttendance(id, {
        action: action,
        rejection_reason: rejection_reason,
      });
      if (response.status === 200) {
        toast.success("Attendance submitted successfully");
        setAttendance((prev) => ({
          ...prev,
          approval_status: action,
          rejection_reason: rejection_reason,
        }));
      } else {
        const msg = extractErrorMessage(response) || MESSAGES.FAILED_SUBMISSION;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.FAILED_SUBMISSION;
      toast.error(msg);
    }
  };

  useEffect(() => {
    const getAttendanceData = async () => {
      try {
        const response = await attendanceService.getDetailedViewById(id);

        if (response?.status === 200 && response.data) {
          setAttendance(response.data);
        } else {
          setAttendance(null);
        }
      } catch (error) {
        setAttendance(null);
      } finally {
        setLoading(false);
      }
    };

    getAttendanceData();
  }, [id]);

  const from = location.state?.from;
  const handleBack = () => {
    if (from) {
      // Hand the list's whole state back (filters plus the selected user/site labels)
      navigate(from, { state: { ...location.state, filters } });
    } else {
      navigate(ATTENDANCE_DASHBOARD_LINK);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center pt-10">
        <Loader size={4} />
      </div>
    );
  if (!attendance)
    return <div className="p-4 text-red-500">{MESSAGES.REQUEST_ERROR}</div>;

  const toPoint = (lat, lng) =>
    lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : null;
  const checkinPoint = toPoint(
    attendance.checkin_latitude,
    attendance.checkin_longitude,
  );
  const checkoutPoint = toPoint(
    attendance.checkout_latitude,
    attendance.checkout_longitude,
  );
  const geofencePath = parseWKTPolygon(attendance.site_geofence).map(
    ([lng, lat]) => ({ lat, lng }),
  );
  const mapPoints = [checkinPoint, checkoutPoint, ...geofencePath].filter(
    Boolean,
  );

  // Zoom out just enough to show the site boundary and both pins
  const fitMapToPoints = (map) => {
    if (mapPoints.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    mapPoints.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);
  };

  return (
    <div>
      <div className="max-w-3xl mx-auto p-6 shadow-md">
        <div className="relative flex items-center h-12 mb-4">
          <button
            type="button"
            onClick={handleBack}
            className="absolute left-0 px-4 py-2 text-primary hover:text-green-600"
          >
            <HiOutlineArrowLeft />
          </button>
          <h2 className="absolute left-1/2 transform -translate-x-1/2 text-xl text-primary font-semibold">
            Detailed View
          </h2>
        </div>
        <Formik
          initialValues={{
            site_name: attendance.site_name || "Non-Site",
            user_name: attendance.user_name,
            date: formatDateDDMMYYYY(attendance.date),
            checkin_time: attendance.checkin_time,
            checkout_time: attendance.checkout_time,
            is_backdated: attendance.is_backdated,
            status: attendance.status
              .toLowerCase()
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
          }}
        >
          <Form>
            <FloatingInput
              label="User Name"
              name="user_name"
              type="text"
              disabled={true}
            />
            <FloatingInput
              label="Site Name"
              name="site_name"
              type="text"
              value={attendance.site_name || "Non-Site"}
              disabled={true}
            />
            <FloatingInput
              label="Date"
              name="date"
              value={formatDateDDMMYYYY(attendance.date)}
              type="text"
              disabled={true}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-3">
              <FloatingInput
                label="Check In Time"
                name="checkin_time"
                value={formatTimeAMPM(attendance.checkin_time)}
                type="text"
                disabled={true}
              />
              <FloatingInput
                label="Check Out Time"
                name="checkout_time"
                value={
                  attendance.checkout_time
                    ? formatTimeAMPM(attendance.checkout_time)
                    : "N/A"
                }
                type="text"
                disabled={true}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-3">
              <FloatingInput
                label="Backdated"
                name="is_backdated"
                disabled={true}
              />
              <FloatingInput
                label="Status"
                name="status"
                type="text"
                disabled={true}
              />
            </div>
          </Form>
        </Formik>
        <div className="flex flex-col md:flex-row w-full justify-around gap-2">
          {/* Check-In Photo */}
          <div className="w-full md:w-1/2">
            <div className="text-primary mb-2 flex items-center gap-2">
              <span>CheckIn Photo</span>
              {attendance?.checkin_photo_url && (
                <button
                  type="button"
                  className="text-primary underline flex items-center gap-1"
                  onClick={() => setShowCheckinPhoto(!showCheckinPhoto)}
                >
                  {showCheckinPhoto ? (
                    <div className="flex items-center gap-1">
                      <BiHide />
                      <span>Hide Photo</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <HiViewfinderCircle />
                      View Photo
                    </div>
                  )}
                </button>
              )}
            </div>
            {attendance?.checkin_photo_url ? (
              <>
                {showCheckinPhoto && (
                  <img
                    src={attendance.checkin_photo_url}
                    alt="check_out"
                    className="w-full max-h-96 object-contain rounded border"
                  />
                )}
              </>
            ) : (
              <div className="text-secondary">
                {MESSAGES.PHOTO_NOT_AVAILABLE}
              </div>
            )}
          </div>

          {/* Check-Out Photo */}
          <div className="w-full md:w-1/2">
            <div className="text-primary mb-2 flex items-center gap-2">
              {" "}
              <span>Checkout Photo</span>
              {attendance?.checkout_photo_url && (
                <button
                  type="button"
                  className="text-primary underline flex items-center gap-1"
                  onClick={() => setShowCheckoutPhoto(!showCheckoutPhoto)}
                >
                  {showCheckoutPhoto ? (
                    <div className="flex items-center gap-1">
                      <BiHide />
                      <span>Hide Photo</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <HiViewfinderCircle />
                      View Photo
                    </div>
                  )}
                </button>
              )}
            </div>
            {attendance.checkout_photo_url ? (
              <>
                {showCheckoutPhoto && (
                  <img
                    src={attendance.checkout_photo_url}
                    alt="check_out"
                    className="w-full max-h-96 object-contain rounded border"
                  />
                )}
              </>
            ) : (
              <div className="text-secondary">
                {MESSAGES.PHOTO_NOT_AVAILABLE}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="text-primary mb-2">Location</div>
          {mapPoints.length === 0 ? (
            <div className="text-secondary">
              {MESSAGES.LOCATION_NOT_AVAILABLE}
            </div>
          ) : isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapPoints[0]}
              zoom={18}
              onLoad={fitMapToPoints}
              options={{
                gestureHandling: "none",
                draggable: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
                keyboardShortcuts: false,
              }}
            >
              {/* Site geofence, same style as the check-in screen */}
              {geofencePath.length > 0 && (
                <Polygon
                  paths={geofencePath}
                  options={{
                    fillColor: "#00BFFF",
                    fillOpacity: 0.3,
                    strokeColor: "#1E90FF",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                  }}
                />
              )}

              {checkinPoint && (
                <Marker position={checkinPoint} label="Check-In" />
              )}

              {checkoutPoint && (
                <Marker position={checkoutPoint} label="Check-Out" />
              )}
            </GoogleMap>
          ) : (
            <div className="flex justify-center pt-10">
              <Loader size={4} />
            </div>
          )}
        </div>

        {attendance.status !== ACTION_APPROVED &&
          attendance.status !== ACTION_REJECTED &&
          attendance.status !== AUTO_APPROVED &&
          nonStaffAccess && (
            <div className="flex justify-center gap-4 pt-4 px-4">
              <button
                className={
                  "px-4 py-2 text-white rounded bg-primary hover:bg-green-700"
                }
                onClick={() => handleApproval(attendance.id, ACTION_APPROVED)}
              >
                Approve
              </button>
              <button
                className={"px-4 py-2 text-white bg-red-500 hover:bg-red-600"}
                onClick={() => setShowRejectPopup(true)}
              >
                Reject
              </button>
            </div>
          )}

        {showRejectPopup && (
          <RejectPopup
            id={attendance.id}
            action={ACTION_REJECTED}
            handleApproval={handleApproval}
            setActiveRejectRow={() => setShowRejectPopup(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DetailedViewPage;
